import React, { useEffect, useState } from "react";
import { charts, Grid, Widget } from "colbi_web_ui/lib/components";
import Spinner from "colbi_web_ui/lib/components/Spinner/Spinner";
import YearSelector from "./YearSelector";
import { Send } from "mosaic-react-icons";
import { sanitizeErrorForLogging } from "../../utils/utils";

const renderAggregateDataWidget = (
    overviewDashboardSummary,
    params,
    i18n,
    year,
    yearType,
    yearSelectorProps,
) => {
    const { organizationId } = params || {};
    const { name: yearName } = year || {};
    const { filterName: yearTypeFilter, name: yearTypeName } = yearType || {};

    return (
        <Widget
            modifiers={["--root-margin-bottom-small", "--root-border-bottom-none"]}
            icon={"comparation"}
            title={i18n`submission status for ${yearTypeName} ${yearName}`}
            sovosIcon={<Send />}
            customHeader={
                <YearSelector
                    years={yearSelectorProps.years}
                    selectedYearType={yearSelectorProps.selectedYearType}
                    selectedYear={yearSelectorProps.selectedYear}
                    setSelectedYearType={yearSelectorProps.setSelectedYearType}
                    setSelectedYear={yearSelectorProps.setSelectedYear}
                    filter={yearSelectorProps.filter}
                    loadingOverview={yearSelectorProps.loadingOverview}
                />
            }
            collapseHeaderOnClick={false}
            setClose={
                overviewDashboardSummary?.length && overviewDashboardSummary.length === 1
                    ? true
                    : false
            }
            children={
                !overviewDashboardSummary ? (
                    <div style={{ display: "flex", justifyContent: "space-around" }}>
                        <Spinner />
                    </div>
                ) : (
                    <Grid
                        modifiers={[
                            "--grid-border-inline",
                            "--grid-margin-none",
                            "--grid-padding-none",
                        ]}
                        cards={[charts.BarChart]}
                        columnsTemplate={[1]}
                        aspect={["30x5"]}
                        labelsPosition={"top"}
                        items={[
                            {
                                data: overviewDashboardSummary,
                                labelsPosition: "inside",
                                stacked: true,
                                summarized: true,
                                links: [
                                    {
                                        filter: [
                                            {
                                                column: "year",
                                                filters: [{ value: yearName }],
                                            },
                                            {
                                                column: "year_type",
                                                filters: [{ value: yearTypeFilter }],
                                            },
                                        ],
                                        to: ["organizations", `${organizationId}`],
                                        filterColumn: "period",
                                        category: "submission",
                                    },
                                ],
                                isOrganizationGraph: true,
                            },
                        ]}
                    />
                )
            }
        />
    );
};

const AggregateDataWidget = ({
    getOrganizationDashboardSummary,
    organizationId,
    selectedYearType,
    selectedYear,
    setSelectedYearType,
    setSelectedYear,
    params,
    organizationModules,
    i18n,
    organizationMaxFiscalYear,
    filter,
}) => {
    const [overviewDashboardSummary, setOverviewDashboardSummary] = useState();
    const [years, setYears] = useState([]);
    const [loadingOverview, setLoadingOverview] = useState(false);

    useEffect(() => {
        if (selectedYearType?.filterName && organizationId && !loadingOverview) {
            setLoadingOverview(true);
            // Request new dashboard
            getOrganizationDashboardSummary(
                organizationId,
                selectedYear?.name,
                selectedYearType.filterName,
            )
                .then((data) => {
                    const { summaries, years: yearsRes } = data || {};

                    if (summaries) {
                        setOverviewDashboardSummary(summaries);
                    }

                    if (JSON.stringify(yearsRes) !== JSON.stringify(years)) {
                        setYears(yearsRes);
                    }
                })
                .catch((error) => {
                    console.error(sanitizeErrorForLogging(error)); // eslint-disable-line no-console
                })
                .finally(() => {
                    setLoadingOverview(false);
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYearType, selectedYear, organizationId]);

    const hasAggregationModule = (organizationModules || []).find(
        (elem) => elem === "aggregate_org_data",
    );

    const showYearSelector = organizationMaxFiscalYear && hasAggregationModule;
    const showAggregationDataWidget =
        organizationModules && selectedYearType && hasAggregationModule;

    return (
        <>
            {showAggregationDataWidget
                ? renderAggregateDataWidget(
                      overviewDashboardSummary,
                      params,
                      i18n,
                      selectedYear,
                      selectedYearType,
                      {
                          years,
                          selectedYearType,
                          selectedYear,
                          setSelectedYearType,
                          setSelectedYear,
                          filter,
                          loadingOverview,
                      },
                  )
                : showYearSelector && <Spinner />}
        </>
    );
};

export default AggregateDataWidget;
